import type { QboTransactionSearchRow } from '@/services/qboTransactionSearch'

export type PendingRowEditClearPatch = Pick<
  QboTransactionSearchRow,
  | 'line_item_category_new'
  | 'line_item_description_new'
  | 'line_item_location_new'
  | 'doc_number_new'
>

/** Clears all pending *NEW edit fields; does not touch update_status. */
export const PENDING_ROW_EDIT_CLEAR_PATCH: PendingRowEditClearPatch = {
  line_item_category_new: undefined,
  line_item_description_new: undefined,
  line_item_location_new: undefined,
  doc_number_new: undefined,
}

export function clearPendingRowEdits(row: QboTransactionSearchRow): QboTransactionSearchRow {
  return { ...row, ...PENDING_ROW_EDIT_CLEAR_PATCH }
}

export function displayCategory(row: QboTransactionSearchRow): string {
  const draft = row.line_item_category_new?.trim()
  return draft ? draft : (row.line_item_category ?? '')
}

export function displayDescription(row: QboTransactionSearchRow): string {
  return row.line_item_description_new !== undefined
    ? row.line_item_description_new
    : (row.line_item_description ?? '')
}

export function supportsHeaderLocationEdit(type: string): boolean {
  return type === 'Bill' || type === 'Purchase'
}

export function displayLocation(row: QboTransactionSearchRow): string {
  return row.line_item_location_new !== undefined
    ? row.line_item_location_new
    : (row.line_item_location || '')
}

export function displayDocNumber(row: QboTransactionSearchRow): string {
  return row.doc_number_new !== undefined ? row.doc_number_new : (row.doc_number ?? '')
}

export function queueCategoryEdit(
  row: QboTransactionSearchRow,
  nextId: string | null
): Pick<QboTransactionSearchRow, 'line_item_category_new'> {
  const current = (row.line_item_category ?? '').trim()
  const trimmed = nextId?.trim()
  if (!trimmed || trimmed === current) {
    return { line_item_category_new: undefined }
  }
  return { line_item_category_new: trimmed }
}

export function queueDescriptionEdit(
  row: QboTransactionSearchRow,
  next: string
): Pick<QboTransactionSearchRow, 'line_item_description_new'> {
  if (next === row.line_item_description || next.trim() === '') {
    return { line_item_description_new: undefined }
  }
  return { line_item_description_new: next }
}

export function queueDocNumberEdit(
  row: QboTransactionSearchRow,
  next: string
): Pick<QboTransactionSearchRow, 'doc_number_new'> {
  const trimmed = next.trim()
  const current = row.doc_number ?? ''
  if (trimmed === current) {
    return { doc_number_new: undefined }
  }
  return { doc_number_new: trimmed }
}

export function queueLocationEdit(
  row: QboTransactionSearchRow,
  nextId: string | null
): Pick<QboTransactionSearchRow, 'line_item_location_new'> {
  const current = row.line_item_location || ''
  if (nextId === null) {
    if (current === '') {
      return { line_item_location_new: undefined }
    }
    return { line_item_location_new: '' }
  }
  if (nextId === current) {
    return { line_item_location_new: undefined }
  }
  return { line_item_location_new: nextId }
}

function isCategoryEditPending(row: QboTransactionSearchRow): boolean {
  const categoryNew = row.line_item_category_new?.trim()
  return Boolean(categoryNew && categoryNew !== (row.line_item_category ?? '').trim())
}

function isDescriptionEditPending(row: QboTransactionSearchRow): boolean {
  const descNew = row.line_item_description_new
  return descNew !== undefined && descNew.trim() !== '' && descNew !== row.line_item_description
}

function isLocationEditPending(row: QboTransactionSearchRow): boolean {
  if (!supportsHeaderLocationEdit(row.type)) return false
  return (
    row.line_item_location_new !== undefined &&
    row.line_item_location_new !== (row.line_item_location || '')
  )
}

function isDocNumberEditPending(row: QboTransactionSearchRow): boolean {
  return (
    row.doc_number_new !== undefined && row.doc_number_new !== (row.doc_number ?? '')
  )
}

/** True when any *NEW field holds a meaningful pending value different from committed QBO values. */
export function hasPendingFieldEdits(row: QboTransactionSearchRow): boolean {
  return (
    isCategoryEditPending(row) ||
    isDescriptionEditPending(row) ||
    isLocationEditPending(row) ||
    isDocNumberEditPending(row)
  )
}

/** True when the row has pending edits not yet applied (or failed and retryable). */
export function rowHasPendingChanges(row: QboTransactionSearchRow): boolean {
  if (!hasPendingFieldEdits(row)) return false
  return !row.update_status || row.update_status === 'Error'
}

export function buildApplyPayloadFields(row: QboTransactionSearchRow): {
  line_item_category_new?: string
  line_item_description_new?: string
  line_item_location_new?: string
  doc_number_new?: string
} {
  const payload: {
    line_item_category_new?: string
    line_item_description_new?: string
    line_item_location_new?: string
    doc_number_new?: string
  } = {}

  if (isCategoryEditPending(row)) {
    payload.line_item_category_new = row.line_item_category_new!.trim()
  }
  if (isDescriptionEditPending(row)) {
    payload.line_item_description_new = row.line_item_description_new
  }
  if (isLocationEditPending(row)) {
    payload.line_item_location_new = row.line_item_location_new
  }
  if (isDocNumberEditPending(row)) {
    payload.doc_number_new = row.doc_number_new
  }

  return payload
}
